import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Sparkles } from "lucide-react";
import Layout from "../components/Layout";
import HeroSplit from "../components/HeroSplit";
import AddressHero from "../components/AddressHero";
import { PLAN_BY_ID } from "../lib/pricing";

export default function Shop() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  // If arriving from /planes with ?plan=X, remember it so the post-lookup flow can use it.
  useEffect(() => {
    const plan = params.get("plan");
    if (plan) localStorage.setItem("velcor_selected_plan", plan);
  }, [params]);

  const selectedPlan = params.get("plan") || localStorage.getItem("velcor_selected_plan") || "";
  const planLabel = selectedPlan
    ? (PLAN_BY_ID[selectedPlan]?.name || selectedPlan)
    : "";

  const title = lang === "es" ? "Verifica tu dirección" : "Verify your address";
  const subtitle = lang === "es"
    ? "Necesitamos tu ESIID para calcular el precio fijo real de tu plan — sin estimaciones engañosas."
    : "We need your ESIID to calculate the real fixed price of your plan — no misleading estimates.";

  return (
    <Layout minimalFooter flushFooter>
      <Helmet>
        <title>{t("nav.plans")} — Velcor Energy</title>
      </Helmet>

      {/* Hero — same dark-photo composition as the home page so the visual
          identity stays consistent across the funnel (plans → shop). */}
      <HeroSplit>
        <section
          className="max-w-[1180px] mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24"
          data-testid="shop-page"
        >
          <div className="text-center max-w-[780px] mx-auto">
            <div className="mb-4 md:mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/35 backdrop-blur-sm ring-1 ring-white/15">
                <Sparkles className="w-3.5 h-3.5 text-velcor-green" />
                <span
                  className="text-[11px] font-extrabold tracking-[0.24em] uppercase text-white"
                  style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
                >
                  {t("plans.kicker")}
                </span>
              </span>
            </div>

            {planLabel && (
              <div className="mb-5 md:mb-6" data-testid="shop-selected-plan">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-velcor-green" />
                  <span className="text-[12px] font-extrabold uppercase tracking-[0.14em] text-velcor-green">
                    {lang === "es" ? "Plan seleccionado" : "Selected plan"}
                  </span>
                  <span className="text-white text-[13px] font-bold">· {planLabel}</span>
                </span>
              </div>
            )}

            <h1
              className="display text-white leading-[1.04]"
              style={{ fontSize: "clamp(30px, 4.2vw, 54px)", letterSpacing: "-0.024em" }}
            >
              {title}
            </h1>
            <p className="mt-4 text-white/80 text-[14.5px] md:text-[16.5px] leading-relaxed max-w-[560px] mx-auto">
              {subtitle}
            </p>
          </div>

          {/* Glass address card — exact same composition as Home's hero card */}
          <div className="mt-8 md:mt-12 mx-auto w-full max-w-[760px]">
            <div
              className="rounded-[32px] p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
              style={{
                background: "rgba(255,255,255,0.94)",
                backdropFilter: "blur(22px) saturate(120%)",
                WebkitBackdropFilter: "blur(22px) saturate(120%)",
              }}
            >
              <AddressHero />
            </div>
          </div>
        </section>
      </HeroSplit>
    </Layout>
  );
}
